import React from "react";
import { AccentDialog } from "./AccentDialog";
import { AccentButton } from "../controls/AccentButton";
import { AccentTooltip } from "../controls/AccentTooltip";
import { AccentFormCtrl } from "../controls/bound/AccentFormCtrl";
import AccentIcons from "../icons/Icons";
import { formHelper } from "../services/HelperService";
import { queryNoTracking } from "../services/DataService";
import { generateTemplateContent } from "../services/TemplateService";
import ChatJobSelector from "../controls/chat/ChatJobSelector";
import ChatWhatsAppNumberSelector from "../controls/chat/ChatWhatsAppNumberSelector";
import { looksLikeHtml, renderSafeHtml } from "../controls/chat/ChatHtml";

const query_Contact_SelectList = queryNoTracking("Contact_SelectList");

const CHANNEL_TO_TEMPLATE_TYPE = {
    whatsapp: "WhatsApp Template",
    sms: "SMS",
    email: "Email",
};

export default function NewChatDlg({
    defaultSubject = "",
    primaryBusinessUnitId = null,
    initialContactID = null,
    initialJob = null,
    initialChannel = "whatsapp",
    initialContactPoint = null,
    channelConfig = null,
    onDone,
    onClose
}) {
    const [subject, setSubject] = React.useState(defaultSubject);
    const [busy, setBusy] = React.useState(false);
    const [touched, setTouched] = React.useState(false);

    // Form state
    const [model, setModel] = React.useState({ ContactID: initialContactID });
    const cboContact = React.useRef(null);
    const [contactInfo, setContactInfo] = React.useState(null);
    const [contactPoint, setContactPoint] = React.useState(initialContactPoint);

    // Sending Phone Number state
    const [phoneNumberConfig, setPhoneNumberConfig] = React.useState(null);

    // Job (single-select) via ChatJobSelector
    const [job, setJob] = React.useState(initialJob);
    const jobMethods = React.useRef(null);

    const [channel, setChannel] = React.useState(initialChannel || "whatsapp");

    // Template selection + preview
    const [template, setTemplate] = React.useState(null);
    const cboTemplate = React.useRef(null);
    const [preview, setPreview] = React.useState("");
    const [previewBusy, setPreviewBusy] = React.useState(false);
    const [previewErr, setPreviewErr] = React.useState("");

    // Validation
    const [probeBusy, setProbeBusy] = React.useState(false);
    const [existingChat, setExistingChat] = React.useState(null);

    // Auto-select first enabled channel if initial channel is disabled
    React.useEffect(() => {
        if (!channelConfig) return;

        const channelEnabled = {
            whatsapp: channelConfig.WhatsAppEnabled,
            sms: channelConfig.SmsEnabled,
            email: channelConfig.EmailEnabled,
        };

        if (!channelEnabled[initialChannel]) {
            const firstEnabled = ['whatsapp', 'sms', 'email'].find(ch => channelEnabled[ch]);
            if (firstEnabled) {
                setChannel(firstEnabled);
            }
        }
    }, [channelConfig, initialChannel]);

    // Helper to check if a channel is enabled
    const isChannelEnabled = React.useCallback((channelName) => {
        if (!channelConfig) return false;
        switch (channelName) {
            case 'whatsapp': return channelConfig.WhatsAppEnabled;
            case 'sms': return channelConfig.SmsEnabled;
            case 'email': return channelConfig.EmailEnabled;
            case 'supply': return channelConfig.SupplyChatEnabled;
            default: return false;
        }
    }, [channelConfig]);

    // Get tooltip text for disabled channels
    const getDisabledTooltip = React.useCallback((channelName) => {
        if (channelName === 'whatsapp') {
            return "WhatsApp has not been configured yet. Please visit the integration settings to get started";
        }
        return `${channelName.charAt(0).toUpperCase() + channelName.slice(1)} messaging coming soon`;
    }, []);

    const errs = React.useMemo(() => ({
        contact: channel === "whatsapp" ? !model.ContactID : false,
        job: channel === "whatsapp" ? !job?.ID : false,
        template: channel === "whatsapp" ? !template?.ID : false,
        contactPoint: (channel === "whatsapp" || channel === "sms") ? !contactPoint : false,
        phoneNumber: channel === "whatsapp" ? !phoneNumberConfig?.ID : false,
    }), [model.ContactID, job?.ID, template?.ID, channel, contactPoint, phoneNumberConfig]);

    const focusFirstError = () => {
        if (errs.contact) {
            cboContact.current?.getFormControl?.()?.focus?.();
            return;
        }
        if (errs.job) {
            document.getElementById("nc-job-anchor")?.scrollIntoView?.({ behavior: "smooth", block: "center" });
            return;
        }
        if (errs.template) {
            cboTemplate.current?.getFormControl?.()?.focus?.();
            return;
        }
    };

    React.useEffect(() => {
        if (!job?.ID || job?.Reference) return;
        let cancelled = false;
        const tryHydrate = () => {
            if (cancelled) return;
            const rows = jobMethods.current?.getData?.() || [];
            if (!rows.length) { setTimeout(tryHydrate, 80); return; }
            const full = rows.find(r => Number(r.ID) === Number(job.ID));
            if (full) setJob(full);
        };
        tryHydrate();
        return () => { cancelled = true; };
    }, [job?.ID, job?.Reference]);

    React.useEffect(() => {
        if (initialContactID) {
            setTimeout(() => cboContact.current?.getFormControl?.()?.setValue?.(initialContactID), 0);
        }
    }, [initialContactID]);

    React.useEffect(() => {
        setContactInfo(null);
        setContactPoint(null);
        if (!model.ContactID) return;
        (async () => {
            const rows = await queryNoTracking("Contact_Info").getAll({ id: model.ContactID });
            const info = Array.isArray(rows) ? rows[0] : rows;
            setContactInfo(info || null);
            const phones = info ? [info.Mobile, info.WorkPhone, info.HomePhone].filter(Boolean) : [];
            if (channel === "whatsapp" || channel === "sms") setContactPoint(phones[0] || null);
            else if (channel === "email") setContactPoint(info?.Email || null);
        })();
    }, [model.ContactID, channel]);

    const getContactQuery = React.useCallback(() => {
        return query_Contact_SelectList.getAllAsQueryableWithCache({
            includeID: model.ContactID,
            companyID: model.CompanyID ?? null
        });
    }, [model.ContactID, model.CompanyID]);

    const contactModel = formHelper.getFieldCtrlModel(
        model,
        "ContactID",
        "select",
        true,
        {
            label: "Contact",
            displayField: "FullName",
            valueField: "ID",
            source: getContactQuery,
            sourceType: "Contact",
            defaultDisplayValue: null,
            placeHolder: "Start typing a name…",
        }
    );

    const getTemplatesQuery = React.useCallback(
        () => {
            // Return the query object itself, don't await it
            const query = queryNoTracking("TemplatesByType").getAllAsQueryable({
                type: CHANNEL_TO_TEMPLATE_TYPE[channel],
                activity: false,
                job: true,
                opportunity: false,
                invoice: false,
                supply: false,
                statement: false,
                webLead: false,
            });

            // Wrap it to add filtering after execution
            return {
                ...query,
                executeWithAlter: async (alterFn) => {
                    // Execute the original query first
                    const result = await query.executeWithAlter(alterFn);

                    // Handle both array and object with data property
                    const dataArray = Array.isArray(result) ? result : (result?.data || []);

                    // Filter out templates with URL buttons
                    const filtered = dataArray.filter(t => {
                        if (!t.Body) return true;
                        return !/<button\s+href=/i.test(t.Body);
                    });

                    // Return in the same format it came in
                    if (Array.isArray(result)) {
                        return filtered;
                    } else {
                        return {
                            data: filtered,
                            total: filtered.length
                        };
                    }
                }
            };
        },
        [channel]
    );

    const templateModel = formHelper.getFieldCtrlModel(
        { TemplateID: template?.ID ?? null },
        "TemplateID",
        "select",
        true,
        {
            label: "Template",
            labelLink: "Template",
            source: getTemplatesQuery,
            sourceType: "Template",
        }
    );

    const redirectToExisting = async () => {
        const payload = { redirectChat: existingChat };
        const res = await onDone?.(payload);
        onClose?.(res ?? payload);
    };

    // Clear ALL form data when channel changes
    React.useEffect(() => {
        // Reset all form state
        setModel({ ContactID: null });
        setContactInfo(null);
        setContactPoint(null);
        setPhoneNumberConfig(null);
        setJob(null);
        setTemplate(null);
        setPreview("");
        setPreviewErr("");
        setExistingChat(null);
        setTouched(false);

        // Clear the control refs
        cboContact.current?.getFormControl?.()?.setValue?.(null);
        cboTemplate.current?.getFormControl?.()?.setValue?.(null);
    }, [channel]);

    React.useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setPreviewErr("");
            if (!template?.ID) {
                setPreview("");
                return;
            }
            try {
                setPreviewBusy(true);
                const result = await generateTemplateContent({
                    UseBody: true,
                    TemplateID: template.ID,
                    ContactID: model.ContactID || null,
                    JobID: job?.ID || null,
                    ActivityID: null,
                    OpportunityID: null,
                    InvoiceID: null,
                    StatementID: null,
                    SupplyJobID: null,
                    WebLeadID: null,
                });
                let body = "";
                if (Array.isArray(result)) {
                    const item = result[0];
                    body = typeof item === "string" ? item : (item?.Body ?? "");
                } else if (typeof result === "string") {
                    body = result;
                } else if (result && typeof result === "object") {
                    body = result.Body ?? "";
                }
                if (!cancelled) setPreview(body || "");
            } catch (e) {
                if (!cancelled) {
                    setPreview("");
                    setPreviewErr("Could not generate preview. Try selecting a contact or job.");
                }
            } finally {
                if (!cancelled) setPreviewBusy(false);
            }
        };

        const t = setTimeout(load, 180);
        return () => {
            cancelled = true;
            clearTimeout(t);
        };
    }, [template?.ID, model.ContactID, job?.ID]);

    React.useEffect(() => {
        let cancelled = false;
        const cp = (contactPoint || "").trim();
        const ch = (channel || "").toLowerCase();
        if (!cp || !ch) { setExistingChat(null); return; }

        (async () => {
            try {
                setProbeBusy(true);
                const res = await queryNoTracking("Chat_All").getAll({
                    skip: 0,
                    take: 1,
                    isArchived: false,
                    contactPoint: cp,
                    channel: ch,
                    status: ["open", "pending", "on_hold"],
                });
                const arr = Array.isArray(res) ? res : [res];
                const first = arr[0];
                const rows = Array.isArray(first?.data) ? first.data : (Array.isArray(first) ? first : []);
                if (!cancelled) {
                    const r = rows[0] || null;
                    const map = r ? {
                        ...r,
                        ID: r.ID ?? r.id ?? r.iD,
                        ChatID: r.ChatID ?? r.chatID ?? r.chatId,
                        Channel: r.Channel ?? r.channel,
                        ContactPoint: r.ContactPoint ?? r.contactPoint,
                        Subject: r.Subject ?? r.subject,
                    } : null;
                    setExistingChat(map);
                }
            } finally {
                if (!cancelled) setProbeBusy(false);
            }
        })();

        return () => { cancelled = true };
    }, [contactPoint, channel]);

    const submit = async () => {
        if (busy) return;

        if (existingChat) {
            setTouched(true);
            return;
        }

        if (errs.contact || errs.job || errs.template || errs.contactPoint || errs.phoneNumber) {
            setTouched(true);
            focusFirstError();
            return;
        }
        try {
            setBusy(true);

            const selectedContact =
                cboContact.current?.getFormControl()?.getSelectedItem?.() ?? null;

            const payload = {
                subject: subject?.trim() || "",
                channel,
                contactID: selectedContact?.ID ?? null,
                contact: selectedContact || null,
                contactPoint,
                jobID: job?.ID ?? null,
                job: job || null,
                templateID: template?.ID ?? null,
                template: template || null,
                templatePreview: preview || "",
                phoneNumberConfigID: phoneNumberConfig?.SenderNumberID ?? null,
            };

            const res = await onDone?.(payload);
            onClose?.(res ?? payload);
        } finally {
            setBusy(false);
        }
    };

    const hasEnabledChannels = React.useMemo(() => {
        if (!channelConfig) return false;
        return channelConfig.WhatsAppEnabled ||
            channelConfig.SmsEnabled ||
            channelConfig.EmailEnabled ||
            channelConfig.SupplyChatEnabled;
    }, [channelConfig]);

    return (
        <AccentDialog
            id="dlg-new-chat"
            headerContent={<strong>New Chat</strong>}
            minWidth={760}
            maxWidth={980}
            onClose={onClose}
            actionsContent={
                hasEnabledChannels ? [
                    <AccentButton key="create" onClick={submit} disabled={busy || !!existingChat}>
                        Create
                    </AccentButton>,
                ] : []
            }
        >
            <div>
                {/* Channel Tabs */}
                <div className="mb-3">
                    <div className="btn-group w-100">
                        {/* WhatsApp */}
                        {isChannelEnabled('whatsapp') ? (
                            <button
                                type="button"
                                className={`btn ${channel === "whatsapp" ? "btn-primary" : "btn-outline-primary"}`}
                                onClick={() => setChannel("whatsapp")}
                                style={{ flex: 1 }}
                            >
                                <AccentIcons.WhatsAppIconOnlyWhiteOnGreenSmall
                                    style={{ width: 18, height: 18, marginRight: 6, marginBottom: 3 }}
                                />
                                WhatsApp
                            </button>
                        ) : (
                            <>
                                <span
                                    id="btn-channel-whatsapp"
                                    style={{ flex: 1, display: 'inline-block' }}
                                >
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary"
                                        style={{ width: '100%', pointerEvents: 'none' }}
                                        disabled={true}
                                    >
                                        <AccentIcons.WhatsAppIconOnlyWhiteOnGreenSmall
                                            style={{ width: 18, height: 18, marginRight: 6, marginBottom: 3 }}
                                        />
                                        WhatsApp
                                    </button>
                                </span>
                                <AccentTooltip
                                    target="btn-channel-whatsapp"
                                    text={getDisabledTooltip('whatsapp')}
                                    placement="top"
                                />
                            </>
                        )}

                        {/* SMS */}
                        {isChannelEnabled('sms') ? (
                            <button
                                type="button"
                                className={`btn ${channel === "sms" ? "btn-primary" : "btn-outline-primary"}`}
                                onClick={() => setChannel("sms")}
                                style={{ flex: 1 }}
                            >
                                <AccentIcons.SMS
                                    style={{ width: 18, height: 18, marginRight: 6, marginBottom: 3 }}
                                />
                                SMS
                            </button>
                        ) : (
                            <>
                                <span
                                    id="btn-channel-sms"
                                    style={{ flex: 1, display: 'inline-block' }}
                                >
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary"
                                        style={{ width: '100%', pointerEvents: 'none' }}
                                        disabled={true}
                                    >
                                        <AccentIcons.SMS
                                            style={{ width: 18, height: 18, marginRight: 6, marginBottom: 3 }}
                                        />
                                        SMS
                                    </button>
                                </span>
                                <AccentTooltip
                                    target="btn-channel-sms"
                                    text={getDisabledTooltip('sms')}
                                    placement="top"
                                />
                            </>
                        )}

                        {/* Email */}
                        {isChannelEnabled('email') ? (
                            <button
                                type="button"
                                className={`btn ${channel === "email" ? "btn-primary" : "btn-outline-primary"}`}
                                onClick={() => setChannel("email")}
                                style={{ flex: 1 }}
                            >
                                <AccentIcons.Email
                                    style={{ width: 18, height: 18, marginRight: 6, marginBottom: 3 }}
                                />
                                Email
                            </button>
                        ) : (
                            <>
                                <span
                                    id="btn-channel-email"
                                    style={{ flex: 1, display: 'inline-block' }}
                                >
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary"
                                        style={{ width: '100%', pointerEvents: 'none' }}
                                        disabled={true}
                                    >
                                        <AccentIcons.Email
                                            style={{ width: 18, height: 18, marginRight: 6, marginBottom: 3 }}
                                        />
                                        Email
                                    </button>
                                </span>
                                <AccentTooltip
                                    target="btn-channel-email"
                                    text={getDisabledTooltip('email')}
                                    placement="top"
                                />
                            </>
                        )}

                        {/* Supply Chat */}
                        <span
                            id="btn-channel-supply"
                            style={{ flex: 1, display: 'inline-block' }}
                        >
                            <button
                                type="button"
                                className="btn btn-outline-primary"
                                style={{ width: '100%', pointerEvents: 'none' }}
                                disabled={true}
                            >
                                Supply Chat
                            </button>
                        </span>
                        <AccentTooltip
                            target="btn-channel-supply"
                            text="Supply chat coming soon"
                            placement="top"
                        />
                    </div>

                    <div className="form-text mt-1">
                        New chat creation options change based on the selected channel
                    </div>
                </div>

                {/* No channels enabled message */}
                {!hasEnabledChannels && (
                    <div className="alert alert-warning mt-4" role="alert">
                        <h5 className="mb-3">
                            <i className="fa fa-exclamation-triangle me-2"></i>
                            No messaging channels configured
                        </h5>
                        <p className="mb-0">
                            No messaging channels are currently enabled. Please configure at least one channel (WhatsApp, SMS, or Email) in the integration settings before creating new chats.
                        </p>
                    </div>
                )}

                {/* Only show form content if channels are enabled */}
                {hasEnabledChannels && (
                    <>
                        {/* Subject */}
                        <div className="mb-3">
                            <label htmlFor="nc-subj" className="form-label">
                                Subject (optional)
                            </label>
                            <input
                                id="nc-subj"
                                type="text"
                                className="form-control"
                                placeholder="Start typing..."
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                            />
                            <div className="form-text mt-1">
                                You can change this later in the chat.
                            </div>
                        </div>

                        {/* WhatsApp Channel Content */}
                        {channel === "whatsapp" && (
                            <>
                                {/* WhatsApp Phone Number selector */}
                                <div className="mt-3">
                                    <ChatWhatsAppNumberSelector
                                        value={phoneNumberConfig?.ID ?? null}
                                        onChange={setPhoneNumberConfig}
                                        currentUserBusinessUnitId={primaryBusinessUnitId}
                                    />
                                    {touched && errs.phoneNumber && (
                                        <div className="form-text text-danger mt-1">Please select a phone number.</div>
                                    )}
                                    <div className="form-text my-1">
                                        Choose which WhatsApp number to send from.
                                    </div>
                                </div>

                                {/* Contact selector */}
                                <AccentFormCtrl
                                    ref={cboContact}
                                    model={contactModel}
                                    onChange={() => setModel({ ...model })}
                                />
                                {touched && errs.contact && (
                                    <div className="form-text text-danger mt-1">Please select a contact.</div>
                                )}

                                {/* Send to */}
                                {contactInfo && (
                                    <div className="mt-2">
                                        <label className="form-label">Send to</label>
                                        <select
                                            className="form-select"
                                            value={contactPoint || ""}
                                            onChange={(e) => setContactPoint(e.target.value || null)}
                                        >
                                            <option value="">Choose…</option>
                                            {contactInfo.Mobile && <option value={contactInfo.Mobile}>Mobile — {contactInfo.Mobile}</option>}
                                            {contactInfo.WorkPhone && <option value={contactInfo.WorkPhone}>Work — {contactInfo.WorkPhone}</option>}
                                            {contactInfo.HomePhone && <option value={contactInfo.HomePhone}>Home — {contactInfo.HomePhone}</option>}
                                        </select>
                                        {touched && errs.contactPoint && (
                                            <div className="form-text text-danger mt-1">Please choose a number to send to.</div>
                                        )}
                                        <div className="form-text">
                                            WhatsApp uses the selected phone number.
                                        </div>
                                    </div>
                                )}

                                {existingChat && (
                                    <div className="alert alert-warning mt-2" role="alert" style={{ lineHeight: 1.35 }}>
                                        An open WhatsApp chat already exists for <strong>{contactInfo?.FullName}</strong> ({contactPoint}).
                                        <div className="mt-2 d-flex gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-primary"
                                                onClick={redirectToExisting}
                                            >
                                                Open Existing Chat
                                            </button>
                                            <span className="text-muted align-self-center">{probeBusy ? "Checking…" : ""}</span>
                                        </div>
                                    </div>
                                )}

                                {/* Job selector */}
                                <div className="mt-3">
                                    <label className="form-label d-block">Link a Job</label>
                                    <span id="nc-job-anchor" />
                                    <ChatJobSelector
                                        contactID={model.ContactID}
                                        value={job?.ID ?? null}
                                        onChange={setJob}
                                        height={300}
                                        gridId="new_chat_job"
                                        methodsRef={jobMethods}
                                    />
                                    {job && (
                                        <div className="form-text mt-1">
                                            Selected job: <strong>{job.Reference}</strong>
                                        </div>
                                    )}
                                    {touched && errs.job && (
                                        <div className="form-text text-danger mt-1">Please select a job.</div>
                                    )}
                                </div>

                                {/* WhatsApp customer service window info */}
                                <div className="alert alert-info mt-4" role="alert" style={{ lineHeight: 1.35 }}>
                                    WhatsApp Customer Service Window: until the contact replies, you can only send <strong>approved templates</strong>. After they respond, you may send free-form messages.
                                </div>

                                {/* Template selector */}
                                <div>
                                    <AccentFormCtrl
                                        key={`template-${channel}`}
                                        ref={cboTemplate}
                                        model={templateModel}
                                        onChange={(value) => {
                                            setTemplate(value ? { ID: value } : null);
                                            setTimeout(() => {
                                                const row = cboTemplate.current?.getFormControl?.()?.getSelectedItem?.() || null;
                                                if (row) setTemplate(row);
                                            }, 0);
                                        }}
                                    />
                                    {touched && errs.template && (
                                        <div className="form-text text-danger mt-1">Please choose a template.</div>
                                    )}
                                    <div className="form-text mt-1">
                                        WhatsApp templates (job-scoped).
                                    </div>
                                </div>

                                {/* Preview panel */}
                                {template && (
                                    <div className="mt-3">
                                        <label className="form-label d-block">Template Preview</label>
                                        <div
                                            className="border rounded p-3"
                                            style={{
                                                minHeight: 140,
                                                whiteSpace: "pre-wrap",
                                                background: "#fafafa",
                                            }}
                                        >
                                            {previewBusy ? (
                                                <span className="text-muted">Generating preview…</span>
                                            ) : preview ? (
                                                looksLikeHtml(preview)
                                                    ? <div className="chat-message-text chat-message-text-html">{renderSafeHtml(preview)}</div>
                                                    : <div style={{ whiteSpace: "pre-wrap" }}>{preview}</div>
                                            ) : previewErr ? (
                                                <span className="text-muted" style={{ fontStyle: "italic" }}>
                                                    {previewErr}
                                                </span>
                                            ) : (
                                                <span className="text-muted">
                                                    Select a contact to generate preview
                                                </span>
                                            )}
                                        </div>
                                        <div className="form-text mt-1">
                                            {!model.ContactID && !job?.ID ? (
                                                <span className="text-warning">
                                                    ⚠️ Select a contact or job to see the personalized preview
                                                </span>
                                            ) : (
                                                "Preview updates as you change Contact or Job."
                                            )}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {/* SMS Channel Content */}
                        {channel === "sms" && (
                            <div className="alert alert-info mt-4 text-center" role="alert">
                                <h5>SMS messaging coming soon</h5>
                                <p className="mb-0">This feature is currently under development.</p>
                            </div>
                        )}

                        {/* Email Channel Content */}
                        {channel === "email" && (
                            <div className="alert alert-info mt-4 text-center" role="alert">
                                <h5>Email messaging coming soon</h5>
                                <p className="mb-0">This feature is currently under development.</p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </AccentDialog>
    );
}